import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Chart } from 'primereact/chart';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { handleGetRequest } from '../service/GetTemplate';
import { endPoints } from '../Config';
import { useDispatch } from 'react-redux';
import { getPreferredLanguage } from '../utilities/languagePreference';
import { translateUiText } from '../utilities/siteTranslations';
import { exportToExcel } from '../utilities/exportToExcel';

const Dashboard = () => {
    const dispatch = useDispatch();
    const [preferredLanguage, setPreferredLanguage] = useState(getPreferredLanguage());
    const statsByTeamRef = useRef(null);
    const statsByRouteRef = useRef(null);
    const t = (value) => translateUiText(value, preferredLanguage);

    // Default to current month
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

    // Filter states
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [fromDate, setFromDate] = useState(monthStart);
    const [toDate, setToDate] = useState(monthEnd);
    const [teamOptions, setTeamOptions] = useState([]);
    // Customer vs internally raised. Mixing them made every headline figure ambiguous.
    const [selectedSource, setSelectedSource] = useState(null);

    // Dashboard stats from API
    const [dashboardStats, setDashboardStats] = useState(null);
    const navigate = useNavigate();

    // Fetch teams for dropdown
    useEffect(() => {
        const fetchTeams = async () => {
            const response = await handleGetRequest(endPoints.getallteams, true, false, dispatch);
            if (response?.data) {
                const options = response.data.map((team) => ({
                    label: team.name || t('Unknown Team'),
                    value: team._id || team.id
                }));
                setTeamOptions(options);
            }
        };
        fetchTeams();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const buildDashboardParams = () => {
        const params = new URLSearchParams();
        if (fromDate instanceof Date) {
            params.append('startDate', `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(2, '0')}-${String(fromDate.getDate()).padStart(2, '0')}`);
        }
        if (toDate instanceof Date) {
            params.append('endDate', `${toDate.getFullYear()}-${String(toDate.getMonth() + 1).padStart(2, '0')}-${String(toDate.getDate()).padStart(2, '0')}`);
        }
        if (selectedTeam) {
            params.append('teamId', selectedTeam);
        }
        if (selectedSource) {
            const [param, value] = String(selectedSource).split(':');
            params.append(param, value);
            if (param === 'raisedBy') params.set('source', 'whatsapp_bot');
        }

        return params.toString();
    };

    const loadDashboardStats = async () => {
        const qs = buildDashboardParams();
        const url = endPoints.getDashboardStats(qs);
        const response = await handleGetRequest(url, true, false, dispatch);
        if (response?.data) {
            setDashboardStats(response.data);
        } else {
            setDashboardStats(null);
        }
    };

    // Initial load + reload on filter change
    useEffect(() => {
        loadDashboardStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTeam, selectedSource, fromDate, toDate]);

    // Reset filters
    const handleReset = () => {
        setSelectedTeam(null);
        setSelectedSource(null);
        setFromDate(monthStart);
        setToDate(monthEnd);
    };

    useEffect(() => {
        const syncPreferredLanguage = () => setPreferredLanguage(getPreferredLanguage());
        window.addEventListener('preferred-language-changed', syncPreferredLanguage);
        window.addEventListener('storage', syncPreferredLanguage);

        return () => {
            window.removeEventListener('preferred-language-changed', syncPreferredLanguage);
            window.removeEventListener('storage', syncPreferredLanguage);
        };
    }, []);

    // Derived numbers for status cards
    const totalOpenTickets = dashboardStats?.statusStat?.totalOpenComplaints || 0;
    const totalClosedTickets = dashboardStats?.statusStat?.totalClosedComplaints || 0;
    const totalAwaitingTickets = dashboardStats?.statusStat?.totalAwaitingComplaints || 0;
    const avgResolutionMinutes = dashboardStats?.statusStat?.overallAvgResolutionMinutes;

    const formatResolutionTime = (minutes) => {
        if (minutes == null) return '-';
        if (minutes < 60) return `${minutes}m`;
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        if (h < 24) return m > 0 ? `${h}h ${m}m` : `${h}h`;
        const d = Math.floor(h / 24);
        const rh = h % 24;
        return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
    };

    // Ticket trend chart (opened vs closed per week)
    const ticketTrendRef = useRef(null);
    // Presets, because moving the window meant operating two date pickers.
    const rangePresets = useMemo(() => {
        const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
        const endOfDay = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };
        const today = new Date();
        return [
            { key: 'today', label: t('Today'), from: () => startOfDay(today), to: () => endOfDay(today) },
            {
                key: 'week',
                label: t('This week'),
                from: () => {
                    const d = new Date(today);
                    const dow = d.getDay() || 7; // Monday-start, matching the trend buckets
                    d.setDate(d.getDate() - dow + 1);
                    return startOfDay(d);
                },
                to: () => endOfDay(today)
            },
            {
                key: 'month',
                label: t('This month'),
                from: () => startOfDay(new Date(today.getFullYear(), today.getMonth(), 1)),
                to: () => endOfDay(new Date(today.getFullYear(), today.getMonth() + 1, 0))
            },
            {
                key: 'last30',
                label: t('Last 30 days'),
                from: () => { const d = new Date(today); d.setDate(d.getDate() - 29); return startOfDay(d); },
                to: () => endOfDay(today)
            }
        ];
    }, [preferredLanguage]);

    // 390 of July's 575 complaints came from customers on WhatsApp; the other 185 were
    // raised by staff in the portal. Counting them together made every headline ambiguous.
    const sourceOptions = useMemo(() => ([
        { label: t('Customer (WhatsApp)'), value: 'raisedBy:customer' },
        { label: t('Operator (WhatsApp)'), value: 'raisedBy:staff' },
        { label: t('Internal (portal)'), value: 'source:manual_portal' },
        { label: 'Llamadas', value: 'source:vapi_call' }
    ]), [preferredLanguage]);

    const formatRangeLabel = (from, to) => {
        if (!(from instanceof Date) || !(to instanceof Date)) return t('All time');
        const fmt = (d) => d.toLocaleDateString(preferredLanguage === 'es' ? 'es-PA' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' });
        return `${fmt(from)} – ${fmt(to)}`;
    };

    // Present-tense counts: what needs doing, deliberately not filtered by the range.
    const actionCards = useMemo(() => {
        const counts = dashboardStats?.actionCounts || {};
        const hours = counts.staleThresholdHours || 72;
        return [
            {
                key: 'awaiting',
                label: t('Waiting on us'),
                value: counts.awaitingAgentReply || 0,
                hint: t('Customer spoke last'),
                icon: 'pi pi-comments',
                color: '#0F8BFD',
                to: '/open-complaints'
            },
            {
                key: 'stale',
                label: t('No reply over 3 days'),
                value: counts.staleWaiting || 0,
                hint: `${t('Waiting more than')} ${Math.round(hours / 24)} ${t('days')}`,
                icon: 'pi pi-exclamation-triangle',
                color: '#FC6161',
                to: '/open-complaints?status=awaiting_customer'
            },
            {
                key: 'finance',
                label: t('In the finance queue'),
                value: counts.financeQueue || 0,
                hint: t('Awaiting a refund decision'),
                icon: 'pi pi-wallet',
                color: '#873EFE',
                to: '/refund-queue'
            },
            {
                key: 'incomplete',
                label: t('Incomplete refunds'),
                value: counts.incompleteRefunds || 0,
                hint: t('Missing bank details'),
                icon: 'pi pi-times-circle',
                color: '#EC4DBC',
                to: '/refund-queue'
            }
        ];
    }, [dashboardStats, preferredLanguage]);

    const ticketTrendChart = useMemo(() => {
        const source = dashboardStats?.ticketTrend || [];
        // Weekly counts are discrete, so they are drawn as bars. A smoothed, filled line
        // over four points invented curvature between weeks that does not exist.
        const labels = source.map((w) => {
            if (w.weekStart) {
                const d = new Date(w.weekStart);
                const label = `${d.getUTCDate()}/${d.getUTCMonth() + 1}`;
                // A week clipped by the date range covers fewer than 7 days; without this
                // its shorter bar reads as a drop in volume.
                return w.partial ? `${label} *` : label;
            }
            return `W${w.week}`;
        });
        return {
            labels,
            datasets: [
                {
                    label: t('Opened'),
                    data: source.map((w) => w.opened || 0),
                    // Solid. At 0.1 alpha these rendered as near-invisible bands: as a
                    // line fill that was subtle, but on a bar it IS the bar.
                    backgroundColor: '#FC6161',
                    borderWidth: 0,
                    // Four weeks across a full-width card makes each bar enormous.
                    maxBarThickness: 56,
                    borderRadius: 4
                },
                {
                    label: t('Closed'),
                    data: source.map((w) => w.closed || 0),
                    backgroundColor: '#0BD18A',
                    borderWidth: 0,
                    maxBarThickness: 56,
                    borderRadius: 4
                }
            ]
        };
    }, [dashboardStats, preferredLanguage]);

    const ticketTrendOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: true, position: 'top' },
            tooltip: {
                callbacks: {
                    footer: (items) => {
                        const label = items?.[0]?.label || '';
                        return label.endsWith('*') ? t('Partial week (cut off by the selected range)') : '';
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: { precision: 0 },
                title: { display: true, text: t('Tickets') }
            },
            x: {
                title: { display: true, text: t('Week starting (Monday)') }
            }
        }
    };

    const statsByTeamChart = useMemo(() => {
        // Sorted, and teams with no tickets in the range are dropped rather than drawn as
        // an invisible bar with a label taking up space.
        const source = (dashboardStats?.statByCategory || []) // statByCategory groups by team
            .filter((c) => (c.total || 0) > 0)
            .sort((a, b) => (b.total || 0) - (a.total || 0));
        const labels = source.map((c) => c._id || t('Unknown'));
        const data = source.map((c) => c.total || 0);
        return {
            labels,
            datasets: [
                {
                    label: t('Number of Complaints'),
                    data,
                    backgroundColor: '#0F8BFD',
                    borderWidth: 0
                }
            ]
        };
    }, [dashboardStats, preferredLanguage]);

    const statsByTeamOptions = {
        indexAxis: 'y', // horizontal, to match the route chart beside it
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false }
        },
        scales: {
            x: {
                beginAtZero: true,
                ticks: { precision: 0 }
            }
        }
    };

    // Stats by Route Bar Chart Data
    const TOP_N_ROUTES = 10;
    const statsByRouteChart = useMemo(() => {
        // Sorted descending and capped: twenty unsorted bars in twenty arbitrary colours
        // meant finding the worst route required reading every one of them. One colour,
        // ranked, so the answer is the top bar.
        const source = [...(dashboardStats?.statByRoute || [])].sort((a, b) => (b.total || 0) - (a.total || 0));
        const top = source.slice(0, TOP_N_ROUTES);
        const rest = source.slice(TOP_N_ROUTES);
        const labels = top.map((r) => r._id || r.route || t('Unknown'));
        const data = top.map((r) => r.total || 0);
        if (rest.length) {
            labels.push(`${t('Others')} (${rest.length})`);
            data.push(rest.reduce((sum, r) => sum + (r.total || 0), 0));
        }
        return {
            labels,
            datasets: [
                {
                    label: t('Number of Complaints'),
                    data,
                    backgroundColor: '#0BD18A',
                    borderWidth: 0
                }
            ]
        };
    }, [dashboardStats, preferredLanguage]);

    const statsByRouteOptions = {
        indexAxis: 'y', // horizontal: route names are long and were rendering rotated
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false }
        },
        scales: {
            x: {
                beginAtZero: true,
                ticks: { precision: 0 }
            }
        }
    };

    // Average Response Time Line Chart Data

    // Support complaints per machine, from machineStat.
    const supportComplaintsData = useMemo(() => {
        const machines = dashboardStats?.machineStat || [];
        // Ranked by volume. Unsorted, this was 215 machines over 22 pages in arbitrary
        // order, so the worst offender appeared on page 1 only by luck and the
        // second-worst could sit on page 17 unseen.
        return machines
            .map((m, idx) => ({
                id: idx + 1,
                route: m.route || '-',
                machineLocation: m.location || '-',
                machineId: m.serialNumber || m.deviceId || '-',
                openComplaints: m.openTickets || 0,
                closedComplaints: m.closedTickets || 0,
                totalComplaints: m.totalTickets || 0
            }))
            .sort((a, b) => b.totalComplaints - a.totalComplaints || b.openComplaints - a.openComplaints);
    }, [dashboardStats]);

    // A machine well above the pack is a maintenance problem, not a support one.
    const HIGH_COMPLAINT_THRESHOLD = 5;
    const machinesNeedingAttention = useMemo(
        () => supportComplaintsData.filter((m) => m.totalComplaints >= HIGH_COMPLAINT_THRESHOLD).length,
        [supportComplaintsData]
    );

    const openComplaintsBody = (row) => (
        <span style={{ fontWeight: row.openComplaints > 0 ? 700 : 400, color: row.openComplaints > 0 ? '#FC6161' : 'inherit' }}>
            {row.openComplaints}
        </span>
    );

    const totalComplaintsBody = (row) => (
        <span style={{ fontWeight: row.totalComplaints >= HIGH_COMPLAINT_THRESHOLD ? 700 : 400 }}>
            {row.totalComplaints}
        </span>
    );

    const formatDateForFilename = (date) => {
        if (!(date instanceof Date)) {
            return 'all';
        }

        return date.toISOString().split('T')[0];
    };

    const getDashboardDateFilter = () => {
        return `_${formatDateForFilename(fromDate)}_to_${formatDateForFilename(toDate)}`;
    };

    const exportSupportComplaints = () => {
        exportToExcel(
            supportComplaintsData.map((row) => ({
                [t('Route')]: row.route,
                [t('Machine Location')]: row.machineLocation,
                [t('Machine ID')]: row.machineId,
                [t('Open Complaints')]: row.openComplaints,
                [t('Closed Complaints')]: row.closedComplaints,
                [t('Total Complaints')]: row.totalComplaints
            })),
            `Support_Complaints_Per_Machine${getDashboardDateFilter()}`,
            null
        );
    };

    return (
        <div className="layout-dashboard p-4">
            <div className="mb-4">
                <h2>{t('Dashboard')}</h2>
                <p className="text-color-secondary">{t('Monitor key metrics, track performance, and view comprehensive analytics for your operations.')}</p>
            </div>
            {/* Filter Section */}
            <div className="card mb-4" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
                <div className="p-4">
                    <div className="grid align-items-end">
                        <div className="col-12 md:col-2">
                            <div className="field">
                                <label htmlFor="team" className="block mb-2 font-semibold text-900">
                                    {t('Team')}
                                </label>
                                <Dropdown
                                    id="team"
                                    value={selectedTeam}
                                    options={teamOptions}
                                    onChange={(e) => setSelectedTeam(e.value)}
                                    optionLabel="label"
                                    placeholder={t('Select Team')}
                                    className="w-full"
                                    showClear
                                    filter
                                />
                            </div>
                        </div>
                        <div className="col-12 md:col-2">
                            <div className="field">
                                <label htmlFor="source" className="block mb-2">{t('Origin')}</label>
                                <Dropdown
                                    id="source"
                                    value={selectedSource}
                                    options={sourceOptions}
                                    onChange={(e) => setSelectedSource(e.value)}
                                    optionLabel="label"
                                    placeholder={t('All origins')}
                                    className="w-full"
                                    showClear
                                />
                            </div>
                        </div>
                        <div className="col-12 md:col-2">
                            <div className="field">
                                <label htmlFor="fromDate" className="block mb-2 font-semibold text-900">
                                    {t('From date')}
                                </label>
                                <Calendar id="fromDate" value={fromDate} onChange={(e) => setFromDate(e.value)} dateFormat="dd/mm/yy" placeholder={preferredLanguage === 'es' ? 'dd/mm/aaaa' : 'dd/mm/yyyy'} className="w-full" showIcon icon="pi pi-calendar" locale={preferredLanguage === 'es' ? 'es' : 'en'} />
                            </div>
                        </div>
                        <div className="col-12 md:col-2">
                            <div className="field">
                                <label htmlFor="toDate" className="block mb-2 font-semibold text-900">
                                    {t('To Date')}
                                </label>
                                <Calendar id="toDate" value={toDate} onChange={(e) => setToDate(e.value)} dateFormat="dd/mm/yy" placeholder={preferredLanguage === 'es' ? 'dd/mm/aaaa' : 'dd/mm/yyyy'} className="w-full" showIcon icon="pi pi-calendar" minDate={fromDate} locale={preferredLanguage === 'es' ? 'es' : 'en'} />
                            </div>
                        </div>
                        <div className="col-12 md:col-2">
                            <div className="field">
                                <Button label={t('Reset')} icon="pi pi-refresh" onClick={handleReset} style={{ backgroundColor: '#00D0DE', borderColor: '#00D0DE', minWidth: '120px' }} />
                            </div>
                        </div>
                    </div>

                    {/* Presets: changing the window required driving two date pickers. */}
                    <div className="flex flex-wrap align-items-center gap-2 mt-2">
                        {rangePresets.map((preset) => (
                            <Button
                                key={preset.key}
                                label={preset.label}
                                className="p-button-sm p-button-outlined"
                                onClick={() => {
                                    setFromDate(preset.from());
                                    setToDate(preset.to());
                                }}
                            />
                        ))}
                        {/* Every figure below was being read as all-time because nothing
                            on the page said which window it covered. */}
                        <span className="ml-auto text-600" style={{ fontSize: '0.9rem' }}>
                            <i className="pi pi-calendar mr-2" />
                            {t('Showing')}: <strong>{formatRangeLabel(fromDate, toDate)}</strong>
                        </span>
                    </div>
                </div>
            </div>

            {/* Needs attention — the numbers you act on, independent of the date range. */}
            <p className="text-600">Atención pendiente ahora · Todas las fechas, equipos y orígenes. Estas tarjetas no cambian con el período seleccionado.</p>
            <div className="grid">
                {actionCards.map((card) => (
                    <div className="col-12 md:col-6 xl:col-3" key={card.key}>
                        <div
                            className="card"
                            role="button"
                            tabIndex={0}
                            onClick={() => navigate(card.to)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(card.to); }}
                            style={{
                                borderRadius: '12px',
                                border: 'none',
                                borderLeft: `4px solid ${card.value > 0 ? card.color : '#cbd5e1'}`,
                                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                                cursor: 'pointer'
                            }}
                        >
                            <div className="flex align-items-center justify-content-between p-3">
                                <div>
                                    <div className="text-600" style={{ fontSize: '0.85rem' }}>{card.label}</div>
                                    <div style={{ fontSize: '1.9rem', fontWeight: 700, color: card.value > 0 ? card.color : '#94a3b8', lineHeight: 1.2 }}>
                                        {card.value}
                                    </div>
                                    <div className="text-500" style={{ fontSize: '0.75rem' }}>{card.hint}</div>
                                </div>
                                <i className={card.icon} style={{ fontSize: '1.6rem', color: card.value > 0 ? card.color : '#cbd5e1' }} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <p className="text-600">Tickets creados en el período seleccionado (Panamá), según su estado actual. Incluye tickets internos según el filtro de origen; excluye conversaciones provisionales y abandonadas. Los pendientes incluyen los casos en espera de cliente.</p>
            <div className="grid">
                {/* Stats Cards */}
                <div className="col-12 md:col-6 xl:col-3">
                    <div
                        className="card"
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            boxShadow: '0 8px 16px rgba(102, 126, 234, 0.3)',
                            borderRadius: '16px',
                            border: 'none',
                            overflow: 'hidden',
                            position: 'relative'
                        }}
                    >
                        <div className="p-4">
                            <div className="flex align-items-center justify-content-between mb-3">
                                <span className="text-white-alpha-90 font-semibold text-lg">Abiertos actualmente</span>
                                <i className="pi pi-ticket text-4xl text-white-alpha-70"></i>
                            </div>
                            <div className="text-5xl font-bold text-white mb-2">{totalOpenTickets}</div>
                            <div className="flex align-items-center text-white-alpha-80">
                                <i className="pi pi-arrow-up mr-2"></i>
                                <span className="text-sm">{t('Active complaints')}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-12 md:col-6 xl:col-3">
                    <div
                        className="card"
                        style={{
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            color: 'white',
                            boxShadow: '0 8px 16px rgba(245, 87, 108, 0.3)',
                            borderRadius: '16px',
                            border: 'none',
                            overflow: 'hidden',
                            position: 'relative'
                        }}
                    >
                        <div className="p-4">
                            <div className="flex align-items-center justify-content-between mb-3">
                                <span className="text-white-alpha-90 font-semibold text-lg">Cerrados actualmente</span>
                                <i className="pi pi-check-circle text-4xl text-white-alpha-70"></i>
                            </div>
                            <div className="text-5xl font-bold text-white mb-2">{totalClosedTickets}</div>
                            <div className="flex align-items-center text-white-alpha-80">
                                <i className="pi pi-check mr-2"></i>
                                <span className="text-sm">De los creados en el período</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-12 md:col-6 xl:col-3">
                    <div
                        className="card"
                        style={{
                            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                            color: 'white',
                            boxShadow: '0 8px 16px rgba(79, 172, 254, 0.3)',
                            borderRadius: '16px',
                            border: 'none',
                            overflow: 'hidden',
                            position: 'relative'
                        }}
                    >
                        <div className="p-4">
                            <div className="flex align-items-center justify-content-between mb-3">
                                <span className="text-white-alpha-90 font-semibold text-lg">Esperando cliente actualmente</span>
                                <i className="pi pi-clock text-4xl text-white-alpha-70"></i>
                            </div>
                            <div className="text-5xl font-bold text-white mb-2">{totalAwaitingTickets}</div>
                            <div className="flex align-items-center text-white-alpha-80">
                                <i className="pi pi-hourglass mr-2"></i>
                                <span className="text-sm">{t('Awaiting response')}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-12 md:col-6 xl:col-3">
                    <div
                        className="card"
                        style={{
                            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                            color: 'white',
                            boxShadow: '0 8px 16px rgba(250, 112, 154, 0.3)',
                            borderRadius: '16px',
                            border: 'none',
                            overflow: 'hidden',
                            position: 'relative'
                        }}
                    >
                        <div className="p-4">
                            <div className="flex align-items-center justify-content-between mb-3">
                                <span className="text-white-alpha-90 font-semibold text-lg">{t('Avg Resolution Time')}</span>
                                <i className="pi pi-stopwatch text-4xl text-white-alpha-70"></i>
                            </div>
                            <div className="text-5xl font-bold text-white mb-2">{formatResolutionTime(avgResolutionMinutes)}</div>
                            <div className="flex align-items-center text-white-alpha-80">
                                <i className="pi pi-history mr-2"></i>
                                <span className="text-sm">{t('Time to close')}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Ticket Trend Chart */}
                <div className="col-12">
                    <div className="card" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: '12px', border: 'none' }}>
                        <div className="card-header pb-3" style={{ borderBottom: '2px solid #f0f0f0' }}>
                            <h5 className="m-0" style={{ color: '#2c3e50', fontWeight: '600' }}>
                                <i className="pi pi-chart-line mr-2" style={{ color: '#FC6161' }}></i>
                                {t('Ticket Trend')}
                            </h5>
                            <p className="text-600 mb-0">Movimientos por semana: creados por fecha de apertura y cerrados por fecha de cierre, incluidos tickets de meses anteriores. No equivale a los totales actuales de arriba.</p>
                        </div>
                        <div className="card-content p-4" style={{ height: '320px' }}>
                            <Chart ref={ticketTrendRef} type="bar" data={ticketTrendChart} options={ticketTrendOptions} style={{ height: '100%', width: '100%' }} />
                        </div>
                    </div>
                </div>

                {/* Stats by Category Bar Chart */}
                <div className="col-12 md:col-6">
                    <div className="card" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: '12px', border: 'none' }}>
                        <div className="card-header pb-3" style={{ borderBottom: '2px solid #f0f0f0' }}>
                            <h5 className="m-0" style={{ color: '#2c3e50', fontWeight: '600' }}>
                                <i className="pi pi-chart-bar mr-2" style={{ color: '#0F8BFD' }}></i>
                                {t('Stats by Team')}
                            </h5>
                        </div>
                        <div className="card-content p-4" style={{ height: '400px' }}>
                            <Chart ref={statsByTeamRef} type="bar" data={statsByTeamChart} options={statsByTeamOptions} style={{ height: '100%', width: '100%' }} />
                        </div>
                    </div>
                </div>

                {/* Stats by Route Bar Chart */}
                <div className="col-12 md:col-6">
                    <div className="card" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: '12px', border: 'none' }}>
                        <div className="card-header pb-3" style={{ borderBottom: '2px solid #f0f0f0' }}>
                            <h5 className="m-0" style={{ color: '#2c3e50', fontWeight: '600' }}>
                                <i className="pi pi-map-marker mr-2" style={{ color: '#0BD18A' }}></i>
                                {t('Stats by Route')}
                            </h5>
                        </div>
                        <div className="card-content p-4" style={{ height: '400px' }}>
                            <Chart ref={statsByRouteRef} type="bar" data={statsByRouteChart} options={statsByRouteOptions} style={{ height: '100%', width: '100%' }} />
                        </div>
                    </div>
                </div>

                {/* Support Complaints Data Table */}
                <div className="col-12">
                    <div className="card" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: '12px', border: 'none' }}>
                        <div className="card-header pb-3 flex justify-content-between align-items-center gap-3 flex-wrap" style={{ borderBottom: '2px solid #f0f0f0' }}>
                            <h5 className="m-0" style={{ color: '#2c3e50', fontWeight: '600' }}>
                                <i className="pi pi-comments mr-2" style={{ color: '#873EFE' }}></i>
                                {t('Support Complaints Per Machine')}
                                <span className="ml-2 text-500" style={{ fontSize: '0.8rem', fontWeight: 400 }}>
                                    {t('Most complaints first')}
                                    {machinesNeedingAttention > 0
                                        ? ` · ${machinesNeedingAttention} ${t('machines with 5 or more')}`
                                        : ''}
                                </span>
                            </h5>
                            <Button
                                label={t('Export to Excel')}
                                icon="pi pi-file-excel"
                                className="p-button-success p-button-sm"
                                onClick={exportSupportComplaints}
                                disabled={supportComplaintsData.length === 0}
                                title={t('Export to Excel')}
                            />
                        </div>
                        <div className="card-content p-4">
                            <DataTable
                                value={supportComplaintsData}
                                paginator
                                rows={25}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                currentPageReportTemplate={t('Showing {first} to {last} of {totalRecords} Records')}
                                emptyMessage={t('No support complaints found.')}
                                responsiveLayout="scroll"
                                className="p-datatable-sm"
                                stripedRows
                            >
                                <Column field="route" header={t('Route')} sortable style={{ fontWeight: '500' }} />
                                <Column field="machineLocation" header={t('Machine Location')} sortable />
                                <Column field="machineId" header={t('Machine ID')} sortable />
                                <Column field="openComplaints" header={t('Open Complaints')} body={openComplaintsBody} sortable />
                                <Column field="closedComplaints" header={t('Closed Complaints')} sortable />
                                <Column field="totalComplaints" header={t('Total Complaints')} body={totalComplaintsBody} sortable />
                            </DataTable>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
